import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, inject, OnInit, Output, ViewChild } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DeviceDetectorService } from 'ngx-device-detector';
import { tap } from 'rxjs/operators';

import { TranslationService } from 'app/core/internationalization/translation.service';
import { ModalService } from 'app/core/modals/services/modal.service';
import { LocalizedToasterService } from 'app/core/toaster/localized-toaster.service';
import { UploadSourceType } from 'app/shared/enums/upload-source-type.enum';
import { IUploadSourceModel } from 'app/shared/models/upload-source.model';
import { NativeAppService } from 'app/shared/services/native-app.service';
import { WebRtcInfoService } from 'app/shared/services/web-rtc-info.service';

import { SelectUploadSourceComponent } from './../../modals/select-upload-source/select-upload-source.component';

import { environment } from 'env/environment';

@UntilDestroy()
@Component({
  selector: 'kh-upload-image',
  styleUrls: ['./upload-image.component.less'],
  templateUrl: './upload-image.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [WebRtcInfoService],
  standalone: false
})
export class UploadImageComponent implements OnInit {
  private readonly nativeAppService = inject(NativeAppService);
  private readonly modalService = inject(ModalService);
  private readonly webRtcInfoService = inject(WebRtcInfoService);
  private readonly deviceDetectorService = inject(DeviceDetectorService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly localizedToasterService = inject(LocalizedToasterService);
  private readonly _translationService = inject(TranslationService);

  @Output() public fileUploaded = new EventEmitter<FileList | Blob[]>();

  @ViewChild('cameraView') public cameraView!: ElementRef;
  @ViewChild('photoCanvas') public photoCanvas!: ElementRef;

  public isWebCamActive = false;
  public isWebCamAvailable = false;
  public isPreviewDisplayed = false;

  // TODO: use @types/w3c-image-capture
  public captureDevice: any;
  public imageBlob!: Blob;

  public ngOnInit(): void {
    if (
      !this.deviceDetectorService.isMobile()
      && this.webRtcInfoService.isAvailable()
      && !this.nativeAppService.isNativeApp()
    ) { // Check for device availability, choose behaviour depending on what we get
      this.webRtcInfoService.getDeviceInfo()
        .pipe(
          tap((result) => {
            this.isWebCamAvailable = result.hasVideo;
            this.changeDetectorRef.markForCheck();
          }), untilDestroyed(this)
        )
        .subscribe();
    }
  }

  public selectImageSource(): void {
    const acceptedExtensions = environment.fileManagerOptions.captureStudentThinking.allowedFileExtensions
      .split(',')
      .map((x: any) => '.' + x)
      .join(',');

    const modal = this.modalService.displayModal<SelectUploadSourceComponent>(SelectUploadSourceComponent, { acceptedExtensions }, 'sm', true);

    modal.content!.closed
      .pipe(
        tap((result: IUploadSourceModel) => this.uploadSourceSelected(result)), untilDestroyed(this)
      )
      .subscribe();
  }

  public selectFile(event: Event): void {
    const target = event.target as HTMLInputElement;
    const files = target.files;

    this.uploadFile(files);
  }

  public cancelSnapshot(): void {
    const srcObject = this.cameraView.nativeElement.srcObject as MediaStream;

    if (srcObject !== null && srcObject !== undefined) {
      srcObject
        .getVideoTracks()
        .forEach((track) => track.stop());
    }

    this.isWebCamActive = false;
  }

  public takeSnapshot(): void {
    this.captureDevice.takePhoto()
      .then((blob: Blob) => {
        this.photoCanvas.nativeElement.src = window.URL.createObjectURL(blob);
        this.imageBlob = blob;
      })
      .catch(console.log);

    this.isPreviewDisplayed = true;
  }

  public uploadSnapshot(): void {
    this.uploadFile([this.imageBlob]);
    this.cancelSnapshot();
  }

  public retrySnapshot(): void {
    this.isPreviewDisplayed = false;
  }

  private uploadSourceSelected(result: IUploadSourceModel): void {
    switch (result.source) {
      case UploadSourceType.WebCam:
        this.useWebCam();
        break;
      case UploadSourceType.File:
        this.uploadFile(result.files);
        break;
    }
  }

  private useWebCam(): void {
    this.isWebCamActive = true;
    this.isPreviewDisplayed = false;
    this.changeDetectorRef.detectChanges();

    navigator.mediaDevices.getUserMedia({ video: true })
      .then((mediaStream) => {
        const videoDevice = mediaStream.getVideoTracks()[0];

        // @ts-expect-error ImageCapture is not yet in TypeScript DOM lib
        this.captureDevice = new ImageCapture(videoDevice);

        this.cameraView.nativeElement.srcObject = mediaStream;
        this.cameraView.nativeElement.play();
      })
      .catch((error) => this.handleWebcamConnectError(error));
  }

  private handleWebcamConnectError(error: any): any {
    const knownErrors: string[] = ['Could not start video source', 'Starting videoinput failed'];
    if (error instanceof Error) {
      const knownError: string | undefined = knownErrors.find((message) => message.includes(error.message));
      if (knownError) {
        window.alert('Could not connect to webcam. Another program might be using it.');
      }
      else {
        console.error(error);
      }
    }
  }

  private uploadFile(files: FileList | Blob[] | null): void {
    if (!files) {
      return;
    }

    const maxFileSize: number = environment.fileManagerOptions.captureStudentThinking.maxUploadSizeMb * 1024 * 1024;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (file.size > maxFileSize) {
        this.localizedToasterService.error(
          this._translationService.instant('UPLOAD_IMAGE.ERROR'),
          this._translationService.instant('UPLOAD_IMAGE.FILE_SIZE_LIMIT_EXCEEDED_THE')
        );
        return;
      }
    }

    this.fileUploaded.emit(files);
  }
}
